import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Gift, Calendar, Check, Clock, X, ClipboardList, MapPin, Eye, Printer, BellRing, Bluetooth } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAppSettings } from "../context/AppSettingsContext";
import { openPickupPrintWindow } from "../lib/pickupReceipt";
import { printPickup, isSupported as thermalSupported } from "../lib/escpos";
import AddressMap from "../components/AddressMap";
import BranchMultiSelect from "../components/BranchMultiSelect";
import SavedViews from "../components/SavedViews";
import Sparkline from "../components/Sparkline";
import DateRangePresets from "../components/DateRangePresets";

function ReservationCustomerAddress({ customerId }) {
  const [c, setC] = React.useState(null);
  React.useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}`).then((r) => setC(r.data)).catch(() => {});
  }, [customerId]);
  if (!c || (!c.address && !c.city)) return null;
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase text-zinc-500 tracking-widest mb-1 flex items-center gap-1">
        <MapPin className="h-3 w-3" /> Indirizzo cliente
      </div>
      <AddressMap customer={c} height={140} />
    </div>
  );
}

const STATUS = {
  pending: { label: "In lavorazione", cls: "pill-gold", icon: Clock },
  ready: { label: "Pronto al ritiro", cls: "pill-mint", icon: Check },
  delivered: { label: "Consegnato", cls: "pill-muted", icon: Check },
  cancelled: { label: "Annullato", cls: "pill-muted", icon: X },
  expired: { label: "Scaduta", cls: "pill-muted", icon: Clock },
};

export default function Reservations() {
  const { user } = useAuth();
  const { settings } = useAppSettings();
  const [detail, setDetail] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [branches, setBranches] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadSeq = React.useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    const params = filter === "all" ? {} : { status_filter: filter };
    const { data } = await api.get("/reservations", { params });
    if (seq === loadSeq.current) setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, [user]);

  const visibleRows = React.useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const fromMs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : 0;
    const toMs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : Infinity;
    return rows.filter((r) => {
      if (branchIds.length > 0 && !branchIds.includes(r.branch_id)) return false;
      if (q) {
        const hay = `${r.customer_name || ""} ${r.customer_card || ""} ${r.customer_phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const t = new Date(r.created_at).getTime();
      return t >= fromMs && t <= toMs;
    });
  }, [rows, customerQuery, dateFrom, dateTo, branchIds]);

  const resetFilters = () => { setCustomerQuery(""); setDateFrom(""); setDateTo(""); setBranchIds([]); };

  const currentFilters = { filter, branchIds, customerQuery, dateFrom, dateTo };
  const applyView = (f) => {
    if (f.filter !== undefined) setFilter(f.filter);
    if (f.branchIds !== undefined) setBranchIds(f.branchIds || []);
    if (f.customerQuery !== undefined) setCustomerQuery(f.customerQuery || "");
    if (f.dateFrom !== undefined) setDateFrom(f.dateFrom || "");
    if (f.dateTo !== undefined) setDateTo(f.dateTo || "");
  };

  const exportFile = async (fmt) => {
    try {
      const params = {};
      if (filter && filter !== "all") params.status_filter = filter;
      if (branchIds.length > 0) params.branch_id = branchIds.join(",");
      if (customerQuery.trim()) params.customer_query = customerQuery.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get(`/reservations/export.${fmt}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `prenotazioni.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} scaricato`);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const exportXlsx = () => exportFile("xlsx");

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/reservations/${id}`, { status });
      toast.success("Aggiornata");
      if (status === "ready" && data?.pickup_code && window.confirm(`Premio pronto · codice ${data.pickup_code}.\nStampare il buono con il QR per il cliente?`)) printQr(data);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const printQr = (r) => { if (!openPickupPrintWindow({ reservation: r, settings })) toast.error("Popup bloccato: consenti le finestre popup per stampare"); };
  const printThermal = async (r) => {
    try { await printPickup({ reservation: r, settings }); toast.success("Buono inviato alla stampante termica"); }
    catch (e) { if (e?.name !== "NotFoundError") toast.error(e?.message || "Stampa termica non riuscita"); }
  };

  const deliver = async (r) => {
    const contribMsg = r.contribution_eur > 0 ? ` + € ${r.contribution_eur.toFixed(2)} contributo` : "";
    if (!window.confirm(
      `Consegnare il premio "${r.reward_name}" a ${r.customer_name}?\n\n` +
      `Verranno scalati ${r.points_cost} pt${contribMsg} dal saldo.`
    )) return;
    try {
      await api.post("/transactions/redeem", {
        customer_id: r.customer_id,
        reward_id: r.reward_id,
        reservation_id: r.id,
        contribution_paid_eur: r.contribution_eur,
      });
      toast.success("Premio consegnato ✅");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/reservations/${id}`);
    toast.success("Eliminata");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reservations-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-mint"><ClipboardList className="h-3 w-3" /> PRENOTAZIONI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Ritiri e riscatti online</h1>
          <p className="text-zinc-400 mt-1">Gestisci le prenotazioni ricevute dal portale cliente.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 flex-wrap">
          {user?.role === "admin" && branches.length > 0 && (
            <div className="w-full sm:w-56" data-testid="reservations-branch-filter">
              <BranchMultiSelect
                branches={branches}
                value={branchIds}
                onChange={setBranchIds}
                testId="reservations-branch"
              />
            </div>
          )}
          <Input
            data-testid="reservations-customer-filter"
            value={customerQuery}
            onChange={(e) => setCustomerQuery(e.target.value)}
            placeholder="🔍 Cliente (nome/carta/tel)"
            className="w-full sm:w-56 bg-zinc-950 border-zinc-800 h-10 text-sm"
          />
          <div className="flex flex-col gap-1">
            <DateRangePresets
              from={dateFrom}
              to={dateTo}
              onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
              testId="reservations-date-preset"
              pageKey="reservations"
            />
            <div className="flex gap-1.5 items-center">
              <Input
                type="date"
                data-testid="reservations-date-from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full sm:w-36 bg-zinc-950 border-zinc-800 h-10 text-xs"
                title="Da"
              />
              <span className="text-zinc-500 text-xs">→</span>
              <Input
                type="date"
                data-testid="reservations-date-to"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full sm:w-36 bg-zinc-950 border-zinc-800 h-10 text-xs"
                title="A"
              />
            </div>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="reservations-filter" className="w-full sm:w-48 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="pending">In lavorazione</SelectItem>
              <SelectItem value="ready">Pronte</SelectItem>
              <SelectItem value="delivered">Consegnate</SelectItem>
              <SelectItem value="cancelled">Annullate</SelectItem>
              <SelectItem value="expired">Scadute</SelectItem>
            </SelectContent>
          </Select>
          {(customerQuery || dateFrom || dateTo || branchIds.length > 0) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              data-testid="reservations-filters-reset"
              className="border-zinc-800 text-zinc-400 hover:text-white h-10"
              title="Azzera filtri"
            >
              ✕ Azzera
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={exportXlsx}
            data-testid="reservations-export-xlsx"
            className="border-emerald-800/50 text-emerald-300 hover:bg-emerald-500/10 h-10"
            title="Esporta Excel con i filtri correnti"
          >
            📥 XLSX
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => exportFile("pdf")}
            data-testid="reservations-export-pdf"
            className="border-red-800/50 text-red-300 hover:bg-red-500/10 h-10"
            title="Esporta PDF con i filtri correnti"
          >
            📄 PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <SavedViews
          page="reservations"
          currentFilters={currentFilters}
          onApply={applyView}
        />
        <div className="flex items-center gap-3 flex-wrap">
          <Sparkline items={visibleRows} label="prenot. ultimi 7g" color="#3B82F6" />
          <div className="text-[11px] text-zinc-500" data-testid="reservations-result-counter">
            <span className="text-emerald-300 font-mono font-bold">{visibleRows.length}</span>
            {visibleRows.length !== rows.length && <> / <span className="font-mono">{rows.length}</span></>}
            {" "}prenotazioni {visibleRows.length !== rows.length ? "corrispondono ai filtri" : "trovate"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleRows.length === 0 && (
          <div className="text-zinc-500 col-span-full text-center py-8">
            {rows.length === 0 ? "Nessuna prenotazione." : "Nessuna prenotazione corrisponde ai filtri."}
          </div>
        )}
        {visibleRows.map((r) => {
          const s = STATUS[r.status] || STATUS.pending;
          const Icon = s.icon;
          return (
            <div key={r.id} className="card-elev p-4 flex flex-col gap-3" data-testid={`reservation-${r.id}`}>
              <div className="flex gap-3">
                {r.reward_image ? (
                  <img src={r.reward_image} alt="" className="h-20 w-20 rounded-lg object-cover" />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Gift className="h-8 w-8 text-amber-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`pill ${s.cls}`}><Icon className="h-3 w-3" /> {s.label}</span>
                    <span className="text-[10px] text-zinc-500 font-mono">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  <div className="font-heading font-bold mt-1 truncate">{r.reward_name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    <span className="text-amber-300 font-mono">{r.points_cost} pt</span>
                    {r.contribution_eur > 0 && <> · <span className="text-red-300 font-mono">+ € {r.contribution_eur.toFixed(2)}</span></>}
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-900 pt-3 text-sm">
                <div className="font-medium">{r.customer_name}</div>
                <div className="text-xs text-zinc-500 font-mono">Carta {r.customer_card} · {r.customer_phone}</div>
                {r.pickup_code && <div className="text-xs mt-0.5"><span className="text-zinc-500">Codice ritiro</span> <span className={`font-mono font-bold ${r.status === "expired" || r.status === "cancelled" ? "line-through text-zinc-500" : r.status === "delivered" ? "text-zinc-400" : "text-amber-300"}`} data-testid={`reservation-code-${r.id}`}>{r.pickup_code}</span>{(r.status === "expired" || r.status === "cancelled") && <span className="ml-1 text-[11px] text-zinc-500">annullato</span>}{r.pickup_reminder_at && <span className="ml-2 text-[11px] text-sky-300 inline-flex items-center gap-1" data-testid={`reservation-reminded-${r.id}`}><BellRing className="h-3 w-3" /> Promemoria inviato il {new Date(r.pickup_reminder_at).toLocaleDateString("it-IT")}</span>}</div>}
                {r.pickup_date && (
                  <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Ritiro: {r.pickup_date}
                  </div>
                )}
                {r.note && <div className="text-xs text-zinc-500 italic mt-1">"{r.note}"</div>}
                <ReservationCustomerAddress customerId={r.customer_id} />
              </div>

              {r.status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`reservation-ready-${r.id}`} size="sm" onClick={() => setStatus(r.id, "ready")} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Pronto
                  </Button>
                  <Button data-testid={`reservation-cancel-${r.id}`} size="sm" variant="outline" onClick={() => setStatus(r.id, "cancelled")} className="border-zinc-800 hover:border-red-500 hover:text-red-300">
                    Annulla
                  </Button>
                </div>
              )}
              {r.status === "ready" && (
                <div className="flex gap-2">
                  <Button data-testid={`reservation-deliver-${r.id}`} size="sm" onClick={() => deliver(r)} className="flex-1 bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
                    <Check className="h-3.5 w-3.5 mr-1" /> Consegna & Scala punti
                  </Button>
                  <Button data-testid={`reservation-print-${r.id}`} size="sm" variant="outline" onClick={() => printQr(r)} className="border-zinc-800" title="Stampa buono con QR">
                    <Printer className="h-3.5 w-3.5" />
                  </Button>
                  {thermalSupported() && <Button data-testid={`reservation-print-thermal-${r.id}`} size="sm" variant="outline" onClick={() => printThermal(r)} className="border-zinc-800" title="Stampa su termica Bluetooth">
                    <Bluetooth className="h-3.5 w-3.5" />
                  </Button>}
                  <Button data-testid={`reservation-cancel-${r.id}`} size="sm" variant="outline" onClick={() => setStatus(r.id, "cancelled")} className="border-zinc-800 hover:border-red-500 hover:text-red-300">
                    Annulla
                  </Button>
                </div>
              )}
              {(r.status === "delivered" || r.status === "cancelled") && (
                <Button data-testid={`reservation-delete-${r.id}`} size="sm" variant="outline" onClick={() => remove(r.id)} className="border-zinc-800">
                  Rimuovi dalla lista
                </Button>
              )}
              <Button
                data-testid={`reservation-view-${r.id}`}
                size="sm" variant="outline"
                onClick={() => setDetail(r)}
                className="border-zinc-800 text-sky-300 hover:bg-sky-500/10"
              >
                <Eye className="h-3.5 w-3.5 mr-1" /> Dettagli
              </Button>
            </div>
          );
        })}
      </div>

      {/* Dialog di dettaglio prenotazione */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg" data-testid="reservation-detail-dialog">
          <DialogHeader><DialogTitle>Dettaglio prenotazione</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                {detail.reward_image ? (
                  <img src={detail.reward_image} alt="" className="h-20 w-20 rounded-lg object-cover" />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Gift className="h-8 w-8 text-amber-300" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-heading font-bold text-lg">{detail.reward_name}</div>
                  <div className="text-xs text-zinc-500 font-mono">
                    <span className="text-amber-300">{detail.points_cost} pt</span>
                    {detail.contribution_eur > 0 && <> · <span className="text-red-300">+ € {detail.contribution_eur.toFixed(2)}</span></>}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">Prenotazione #{detail.id?.slice(0,8)}</div>
                </div>
              </div>
              <div className="border-t border-zinc-800 pt-3 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Cliente</div><div className="font-medium">{detail.customer_name}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Carta</div><div className="font-mono">{detail.customer_card}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Telefono</div><div className="font-mono">{detail.customer_phone}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Stato</div><div>{STATUS[detail.status]?.label || detail.status}</div></div>
                <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Data richiesta</div><div>{new Date(detail.created_at).toLocaleString("it-IT")}</div></div>
                {detail.pickup_date && <div><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Ritiro</div><div>{detail.pickup_date}</div></div>}
                {detail.branch_id && <div className="col-span-2"><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Sede</div><div className="font-mono">{detail.branch_id}</div></div>}
                {detail.delivered_at && <div className="col-span-2"><div className="text-zinc-500 text-[10px] uppercase tracking-widest">Consegnato il</div><div>{new Date(detail.delivered_at).toLocaleString("it-IT")}</div></div>}
              </div>
              {detail.note && (
                <div className="border-t border-zinc-800 pt-3">
                  <div className="text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Note del cliente</div>
                  <div className="text-xs italic text-zinc-300">"{detail.note}"</div>
                </div>
              )}
              <ReservationCustomerAddress customerId={detail.customer_id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
